import { useCreatePlacement } from "@/hooks/mutations/useCreatePlacement";
import { useCreateView } from "@/hooks/mutations/useCreateView";
import { useUpdatePlacement } from "@/hooks/mutations/useUpdatePlacement";
import { useUpdateView } from "@/hooks/mutations/useUpdateView";
import { useAddPlacement } from "@/hooks/sets/useAddPlacement";
import { useAddView } from "@/hooks/sets/useAddView";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import { useSetView } from "@/hooks/sets/useSetView";
import {
  defaultPlacementQueryParams,
  defaultRequisitionQueryParams,
  defaultRequisitionStatusesForOffers,
} from "@/lib/constants";
import {
  CandidacyPlacement,
  CreateCandidacyPlacementRequest,
  CreateViewRequest,
  UpdateCandidacyPlacementRequest,
  UpdateViewRequest,
  View,
  ViewQueryParams,
} from "@/types";

// Adds
type AddPlacementFn = ReturnType<typeof useAddPlacement>;
type AddViewFn = ReturnType<typeof useAddView>;

// Creates
type CreatePlacementHook = ReturnType<typeof useCreatePlacement>;
type CreatePlacementMutationFn = CreatePlacementHook["mutate"];
type CreateViewHook = ReturnType<typeof useCreateView>;
type CreateViewMutationFn = CreateViewHook["mutate"];

// Sets
type SetCandidacyInfoFn = ReturnType<typeof useSetCandidacyInfo>;
type SetPlacementFn = ReturnType<typeof useSetPlacement>;
type SetRequisitionFn = ReturnType<typeof useSetRequisition>;
type SetViewFn = ReturnType<typeof useSetView>;

// Updates
type UpdatePlacementHook = ReturnType<typeof useUpdatePlacement>;
type UpdatePlacementMutationFn = UpdatePlacementHook["mutate"];
type UpdateViewHook = ReturnType<typeof useUpdateView>;
type UpdateViewMutationFn = UpdateViewHook["mutate"];

export const mutations = {
  /**
   * A function used in status change dialogs to create a placement and update the cache accordingly
   */
  createPlacement: ({
    addPlacement,
    createPlacement,
    placementRequest,
    setCandidacyInfo,
    onSuccess,
  }: {
    addPlacement: AddPlacementFn;
    createPlacement: CreatePlacementMutationFn;
    placementRequest: CreateCandidacyPlacementRequest;
    setCandidacyInfo: SetCandidacyInfoFn;
    onSuccess?: (placement: CandidacyPlacement) => void;
  }) => {
    createPlacement(
      {
        createPlacementRequest: placementRequest,
      },
      {
        onSuccess: (newPlacement) => {
          addPlacement({
            newPlacement,
            placementQueryParams: defaultPlacementQueryParams,
          });

          setCandidacyInfo({
            candidacyId: newPlacement.candidacy.id,
            status: newPlacement.candidacy.status,
          });
          onSuccess?.(newPlacement);
        },
      },
    );
  },

  /**
   * Function used to create a view and update the cache accordingly
   */
  createView: ({
    addView,
    createView,
    createViewRequest,
    setSelectedView,
    viewQueryParams,
  }: {
    addView: AddViewFn;
    createView: CreateViewMutationFn;
    createViewRequest: CreateViewRequest;
    setSelectedView: (view: View) => void;
    viewQueryParams: ViewQueryParams;
  }) => {
    createView(
      {
        createViewRequest,
      },
      {
        onSuccess: (newView) => {
          addView({ newView, viewQueryParams });
          setSelectedView(newView);
        },
      },
    );
  },

  /**
   * A function used in status change dialogs to update a placement and update the cache accordingly
   */
  updatePlacement: ({
    placementId,
    setCandidacyInfo,
    setPlacement,
    setRequisition,
    updatePlacement,
    updatePlacementRequest,
    onSuccess,
  }: {
    placementId: number;
    // This is optional because not every change to a placement results in a candidacy status change.
    setCandidacyInfo?: SetCandidacyInfoFn;
    setPlacement: SetPlacementFn;
    setRequisition: SetRequisitionFn;
    updatePlacement: UpdatePlacementMutationFn;
    updatePlacementRequest: UpdateCandidacyPlacementRequest;
    onSuccess?: () => void;
  }) => {
    updatePlacement(
      {
        id: placementId,
        updatePlacementRequest,
      },
      {
        onSuccess: (updatedPlacement) => {
          setPlacement({
            updatedPlacement,
            placementQueryParams: defaultPlacementQueryParams,
          });
          setRequisition({
            updatedRequisition: updatedPlacement.requisition,
            requisitionQueryParams: {
              ...defaultRequisitionQueryParams,
              statuses: defaultRequisitionStatusesForOffers,
            },
          });
          setCandidacyInfo?.({
            candidacyId: updatedPlacement.candidacy.id,
            status: updatedPlacement.candidacy.status,
          });
          onSuccess?.();
        },
      },
    );
  },

  /**
   * Function used to update a view and update the cache accordingly
   */
  updateView: ({
    setSelectedView,
    setView,
    updateView,
    updateViewRequest,
    viewId,
    viewQueryParams,
  }: {
    setSelectedView: (view: View) => void;
    setView: SetViewFn;
    updateView: UpdateViewMutationFn;
    updateViewRequest: UpdateViewRequest;
    viewId: number;
    viewQueryParams: ViewQueryParams;
  }) => {
    updateView(
      {
        id: viewId,
        updateViewRequest,
      },
      {
        onSuccess: (updatedView) => {
          setSelectedView(updatedView);
          setView({ updatedView, viewQueryParams });
        },
      },
    );
  },
};
